import { Helmet } from "react-helmet-async";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { 
  Shield, 
  CheckCircle2, 
  Users, 
  FileText, 
  AlertTriangle,
  Phone,
  ArrowLeft,
  ArrowRight,
  Building2,
  Scale,
  ClipboardList,
  TrendingUp,
  Clock,
  BookOpen,
  GraduationCap,
  AlertCircle
} from "lucide-react";
import { LucideIcon } from "lucide-react";

const iconMap: Record<string, LucideIcon> = {
  ClipboardList,
  FileText,
  Users,
  AlertTriangle,
  Scale,
  TrendingUp,
  GraduationCap,
  BookOpen,
  Clock,
  AlertCircle
};

export default function SafetyOfficer() {
  const { t, i18n } = useTranslation('services-pages');
  const isRTL = i18n.language === 'he';
  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  const responsibilities = t('safetyOfficer.responsibilities', { returnObjects: true }) as Array<{
    icon: string;
    title: string;
    description: string;
  }>;

  const legalRequirements = t('safetyOfficer.legalRequirements', { returnObjects: true }) as Array<{
    title: string;
    description: string;
    icon: string;
  }>;

  const processSteps = t('safetyOfficer.processSteps', { returnObjects: true }) as Array<{
    step: string;
    title: string;
    description: string;
  }>;

  const faqs = t('safetyOfficer.faqs', { returnObjects: true }) as Array<{
    question: string;
    answer: string;
  }>;

  return (
    <Layout>
      <Helmet>
        <title>{t('safetyOfficer.meta.title')}</title>
        <meta name="description" content={t('safetyOfficer.meta.description')} />
        <link rel="canonical" href="https://adam-safety.com/services/safety-officer" />
        <meta property="og:title" content={t('safetyOfficer.meta.title')} />
        <meta property="og:description" content={t('safetyOfficer.meta.description')} />
        <meta property="og:url" content="https://adam-safety.com/services/safety-officer" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('safetyOfficer.meta.title')} />
        <meta name="twitter:description" content={t('safetyOfficer.meta.description')} />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 z-0"
          style={{ background: 'var(--gradient-hero)' }}
        >
          <div className="absolute inset-0 pattern-hero opacity-30" />
        </div>

        <div className="container relative z-10 px-4 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/20 text-accent mb-6">
              <Shield className="w-5 h-5" />
              <span className="text-sm font-medium">{t('safetyOfficer.hero.badge')}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-foreground mb-6">
              {t('safetyOfficer.hero.title')}
            </h1>
            
            <p className="text-xl md:text-2xl text-primary-foreground/80 mb-8 max-w-3xl mx-auto leading-relaxed">
              {t('safetyOfficer.hero.subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  {t('safetyOfficer.hero.cta')}
                </Button>
              </Link>
              <Link to="/services/external-consulting">
                <Button variant="outline" size="lg" className="gap-2 text-lg px-8 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10">
                  {t('safetyOfficer.hero.secondaryCta')}
                  <ArrowIcon className="w-5 h-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What is a Safety Officer */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                {t('safetyOfficer.whatIs.title')}
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                {t('safetyOfficer.whatIs.description')}
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 items-start">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                <div className="card-glass p-6">
                  <div className="flex items-start gap-4">
                    <div className="icon-container">
                      <Building2 className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-foreground mb-2">{t('safetyOfficer.whatIs.authority.title')}</h3>
                      <p className="text-muted-foreground">
                        {t('safetyOfficer.whatIs.authority.description')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card-glass p-6">
                  <div className="flex items-start gap-4">
                    <div className="icon-container">
                      <Scale className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-foreground mb-2">{t('safetyOfficer.whatIs.legal.title')}</h3>
                      <p className="text-muted-foreground">
                        {t('safetyOfficer.whatIs.legal.description')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="card-glass p-6">
                  <div className="flex items-start gap-4">
                    <div className="icon-container">
                      <Clock className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-foreground mb-2">{t('safetyOfficer.whatIs.ongoing.title')}</h3>
                      <p className="text-muted-foreground">
                        {t('safetyOfficer.whatIs.ongoing.description')}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
              >
                <div className="bg-primary rounded-3xl p-8 text-primary-foreground mb-6">
                  <h3 className="text-2xl font-bold mb-6">{t('safetyOfficer.legalRequirementsTitle')}</h3>
                  <div className="space-y-4">
                    {legalRequirements.map((req, index) => {
                      const IconComponent = iconMap[req.icon] || AlertCircle;
                      return (
                        <div key={index} className="flex items-start gap-3">
                          <IconComponent className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                          <div>
                            <span className="font-semibold text-primary-foreground">{req.title}</span>
                            <p className="text-sm text-primary-foreground/80">{req.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                <div className="bg-muted/50 rounded-xl p-4 text-sm text-muted-foreground">
                  <p className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>
                      {t('safetyOfficer.disclaimer')}
                    </span>
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Responsibilities */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('safetyOfficer.responsibilitiesTitle')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('safetyOfficer.responsibilitiesSubtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {responsibilities.map((item, index) => {
              const IconComponent = iconMap[item.icon] || ClipboardList;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="card-service"
                >
                  <div className="icon-container mb-4">
                    <IconComponent className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-8 text-center"
          >
            <Link to="/services/safety-officer-roles">
              <Button variant="outline" className="gap-2">
                {t('safetyOfficer.viewFullList')}
                <ArrowIcon className="w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Our Process */}
      <section className="section-padding bg-background">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('safetyOfficer.processTitle')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('safetyOfficer.processSubtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="card-glass p-6 h-full">
                  <div className="text-5xl font-bold text-accent/20 mb-4">{step.step}</div>
                  <h3 className="text-xl font-bold text-foreground mb-2">{step.title}</h3>
                  <p className="text-muted-foreground text-sm">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding section-alt">
        <div className="container px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('safetyOfficer.faqTitle')}
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card-glass p-6"
              >
                <h3 className="text-lg font-bold text-foreground mb-2">{faq.question}</h3>
                <p className="text-muted-foreground">{faq.answer}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary">
        <div className="container px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              {t('safetyOfficer.cta.title')}
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-8">
              {t('safetyOfficer.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button variant="cta" size="lg" className="gap-2 text-lg px-8">
                  <Phone className="w-5 h-5" />
                  {t('safetyOfficer.cta.button')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
